import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const SSOCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const lr_token = searchParams.get('lr-token');
        
        if (!lr_token) {
          throw new Error('No token received from SSO provider');
        }

        // Exchange the token
        const response = await fetch(`${API_URL}/api/v1/auth/exchange-token?lr_token=${lr_token}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to exchange token');
        }

        const data = await response.json();
        
        if (!data.access_token) {
          throw new Error('No access token received');
        }

        // Store the access token
        localStorage.setItem('accessToken', data.access_token);
        login(data.access_token);

        // Fetch profile data
        await fetchProfile(data.access_token);

        // Redirect to dashboard
        navigate('/dashboard');
      } catch (error) {
        console.error('SSO callback error:', error);
        setError(error.message);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, login]);

  const fetchProfile = async (token) => {
    try {
      const response = await fetch(
        `${API_URL}/api/v1/auth/profile?access_token=${encodeURIComponent(token)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();

      const formattedData = {
        name: data.Profile.FullName || `${data.Profile.FirstName} ${data.Profile.LastName}`.trim(),
        email: data.Profile.Email?.[0]?.Value || '',
        id: data.Profile.ID,
        uid: data.Profile.Uid,
        provider: data.Profile.Provider,
        Profile: data.Profile,
        Orgs: data.Orgs || []
      };

      localStorage.setItem('profileData', JSON.stringify(formattedData));

      // Set default society if available
      if (formattedData.Orgs?.length > 0) {
        localStorage.setItem('societyId', formattedData.Orgs[0].OrgId);
        // Dispatch event to notify society selection
        window.dispatchEvent(new Event('societyChanged'));
      }

      // Small delay to ensure data is set before redirect
      await new Promise(resolve => setTimeout(resolve, 100));
      
    } catch (error) {
      console.error('Error fetching profile:', error);
      throw new Error('Failed to fetch profile data');
    }
  };

  if (error) {
    return (
      <div className="sso-callback-error">
        <h2>Authentication Error</h2>
        <p>{error}</p>
        <p>Redirecting to login...</p>
      </div>
    );
  }

  return (
    <div className="sso-callback-loading">
      <h2>Completing Authentication</h2>
      <p>Please wait while we complete the process...</p>
    </div>
  );
};

export default SSOCallback; 